import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { BRAND, IMAGES } from "@/data";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.03]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden"
      style={{ backgroundColor: "var(--bg-primary)" }}
    >
      <div className="mx-auto max-w-[1400px] w-full px-6 md:px-10 pt-32 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="text-sm tracking-[0.28em] uppercase mb-8"
            style={{ color: "var(--rose-mocha-deep)" }}
          >
            {BRAND.tagline}
          </motion.p>

          <h1
            className="font-serif tracking-tight leading-[0.95] text-[15vw] sm:text-[11vw] lg:text-[6.6rem]"
            style={{ color: "var(--text-primary)" }}
          >
            <MaskLines lines={["ProExec", "Virtual"]} delay={0.3} />
            <span className="block overflow-hidden">
              <motion.span
                className="block italic"
                style={{ color: "var(--rose-mocha)" }}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.54, ease: [0.22, 1, 0.36, 1] }}
              >
                Solutions
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 max-w-2xl"
          >
            <p
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: "var(--text-secondary)" }}
            >
              {BRAND.intro}
            </p>
            <a
              href="#packages"
              data-testid="hero-cta"
              className="group shrink-0 inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 text-sm font-medium transition-colors duration-300"
              style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
            >
              View packages
              <span
                className="grid place-items-center w-9 h-9 rounded-full transition-transform duration-300 group-hover:rotate-45"
                style={{ backgroundColor: "var(--accent-gold)" }}
              >
                <ArrowDownRight size={18} color="#2E241D" />
              </span>
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="img-zoom-wrap relative rounded-t-[140px] rounded-b-2xl overflow-hidden h-[46vh] lg:h-[62vh]"
            style={{ border: "1px solid var(--border-light)" }}
          >
            <motion.img
              src={IMAGES.heroAccent}
              alt="Elegant executive workspace with warm tones"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[110%] object-cover object-[center_38%]"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
